import { useEffect, useState } from 'react';
import { landingApiService, type LandingSnapshot } from '../services/api/landingApiService';

// Module-level cache so every section shares a single network request.
// The hook NEVER throws — on any failure it returns null and each section
// falls back to its hardcoded FALLBACK_* constants.
// The cache expires so a long-lived tab eventually picks up CMS edits instead
// of pinning whatever snapshot happened to be live when the page was opened.
const CACHE_TTL_MS = 60_000;

let cache: LandingSnapshot | null = null;
let cachedAt = 0;
let inflight: Promise<LandingSnapshot | null> | null = null;

function isFresh() {
  return cache !== null && Date.now() - cachedAt < CACHE_TTL_MS;
}

async function loadSnapshot(): Promise<LandingSnapshot | null> {
  if (isFresh()) return cache;
  if (!inflight) {
    inflight = landingApiService
      .getSnapshot()
      .then((data) => {
        cache = data;
        cachedAt = Date.now();
        return data;
      })
      // Keep serving the last good snapshot rather than dropping to the
      // hardcoded fallbacks when a background refresh fails.
      .catch(() => cache)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export interface UseLandingContentResult {
  data: LandingSnapshot | null;
  loading: boolean;
}

export function useLandingContent(): UseLandingContentResult {
  const [data, setData] = useState<LandingSnapshot | null>(cache);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let active = true;
    // loadSnapshot resolves immediately with the module cache when fresh,
    // so we never call setState synchronously inside the effect body.
    const refresh = () => {
      loadSnapshot().then((snapshot) => {
        if (!active) return;
        setData(snapshot);
        setLoading(false);
      });
    };

    refresh();

    const onFocus = () => {
      if (document.visibilityState === 'visible' && !isFresh()) refresh();
    };
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  return { data, loading };
}

export default useLandingContent;
